import {DashboardWFData, DashboardWFParams, getDashboardWF, getMonitorWF, listMonitorWF, MonitorWFParams} from "@commons/services/presenze_api/dashboard.api";
import {UseQueryOptions} from "@tanstack/react-query/src/types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios, {AxiosError, AxiosResponse} from "axios";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {CalendarioChiusureRecord} from "@commons/mapping/CalendarioChiusureRecord";
import {getCalendarioChiusureByAnno} from "@commons/services/presenze_api/tabellePresenze.api";
import {config} from "@commons/config.instance";
import {DateTime, Interval} from "luxon";
import {FPNetDate, IndiceMese, Periodo} from "@commons/types";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectUser} from "@commons/core.slice";
import {useCallback, useMemo} from "react";
import {MonitorWFData} from "@commons/services/presenze_api/monitorWF.api";
import {_isMonitorWFEditabile} from "@commons/utils/isEditabile";

export function useDashboardWF(params: DashboardWFParams, opt?: UseQueryOptions) {
    return useQuery<DashboardWFData, Error>({
        queryKey: ['dashboardWF', params],
        queryFn: () => getDashboardWF(params),
        //refetchOnMount: true,
        enabled: !!DashboardWFParams.isValid(params),
    })
}

export function useMonitorWF(params: MonitorWFParams, opt?: UseQueryOptions) {
    return useQuery<MonitorWFData, Error>({
        queryKey: ['monitorWF', params],
        queryFn: () => listMonitorWF(params, true),
        enabled: !!params.idDip,
    })
}

export function useSingolaRichiestaWF(params: string | null, opt?: UseQueryOptions) {
    const query = useQuery<PresenzeCausaleWFRecord, Error>({
        queryKey: ['singolaRichiestaWF', params],
        queryFn: () => getMonitorWF(params, true),
        enabled: !!params,
        //staleTime: 1000 * 10 * 1,
        //staleTime: Infinity,
    })

    /*useEffect(() => {
        if (query.isFetched && query.isStale)
            query.refetch();
    }, [query, query.isStale])*/

    return query;
}

/**
 * hook per la MODIFICA e CANCELLAZIONE della richiesta
 * N.B. a seconda dell'uri passato, si ottiene la modifica della richiesta o il suo annullamento (la cancellazione consiste nel modificare alcune properties)
 * la modifica è gestità dal path https://fpnet2.ericed.it/FPNet2021/tool/{idAziendaMaster}-{idAzienda}/workflow/{idWF}-{data}
 * la cancellazione è gestità dal path https://fpnet2.ericed.it/FPNet2021/prePLUS/presenze/{idDip}/causaleWF/{idWF}-{data}
 * @param uri
 * @param monitorWFParams
 */
export function usePutSingolaRichiestaWF(uri?: string | null, monitorWFParams?: MonitorWFParams) {
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse<PresenzeCausaleWFRecord>, AxiosError, PresenzeCausaleWFRecord>({
        mutationFn: (richiestaAggiornata) => {
            const finalURI = uri || config.getBaseUrl('FPNet') + `/tool/${richiestaAggiornata.idAziendaMaster}-${richiestaAggiornata.idAzienda}/workflow/${richiestaAggiornata.wf.idWF}-${DateTime.fromISO(richiestaAggiornata.giornoDt!).toFormat('yyyyLLdd')}`;
            return axios.put(finalURI, richiestaAggiornata);
        },
        onSuccess: (result, variables, context) => {
            // Replace optimistic!
            queryClient.setQueryData<PresenzeCausaleWFRecord>(['singolaRichiestaWF', uri], (old) => {
                    return old ? {...old, ...variables, ...result.data} : {...variables, ...result.data};
                }
            )
            /*queryClient.setQueryData<MonitorWFData>(['monitorWF', monitorWFParams], (oldList) => {
                    const newList = cloneable.deepCopy(oldList)!;
                    newList.data = newList.data.map((richiesta) => {
                        if (richiesta.URI === uri) {
                            //richiesta.wf.stato=; // Nuovo valore da "richiestaAggiornata" appena aggiornata
                        }
                        return richiesta;
                    })
                    return newList;
                }
            )*/
            // https://nurbol.dev/posts/refetching-vs-invalidating-queries
            // https://tanstack.com/query/v4/docs/react/guides/invalidations-from-mutations
            //queryClient.invalidateQueries({queryKey: ['singolaRichiestaWF', uri]});
            if (monitorWFParams)
                queryClient.invalidateQueries({queryKey: ['monitorWF', monitorWFParams]});
            queryClient.invalidateQueries({queryKey: ['monitorWFTable']});
            queryClient.invalidateQueries({queryKey: ['dashboardWF']})

            // todo da rivedere!
            //toast('Richiesta aggiornata correttamente!', {type: 'success'});
        },
        onError: (error, variables, context) => {
            // todo da rivedere!
            // An error happened!
            /*if (error.response?.status === 403)
                toastAsError(error.response.data as string);
            if (error.response?.status === 500) { // @ts-ignore
                toastAsError(error.response.data.message);
            }*/
        },
    })
}

export function usePostSingolaRichiestaWF(monitorWFParams?: MonitorWFParams) {
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse<PresenzeCausaleWFRecord>, AxiosError, PresenzeCausaleWFRecord>({
        mutationFn: (richiestaAggiornata) => {
            const url = config.getBaseUrl('FPNet') + `/prePLUS/presenze/${richiestaAggiornata.id}/causaleWF`;
            return axios.post(url, richiestaAggiornata);
        },
        onSuccess: (result, variables, context) => {
            if (monitorWFParams)
                queryClient.invalidateQueries({queryKey: ['monitorWF', monitorWFParams]});
            queryClient.invalidateQueries({queryKey: ['dashboardWF']})

            return result;
        },
        onError: (error, variables, context) => {
            // todo da rivedere!
            // An error happened!
            /*if (error.response?.status === 403)
                toastAsError(error.response.data as string);
            if (error.response?.status === 500) { // @ts-ignore
                toastAsError(error.response.data.message);
            }*/
        },
    })
}

export interface CalendarioChiusureParams {
    idAzienda: number | undefined | null,
    anno: number
}

/**
 * Hook per prelievo calendario e metodi specifici utili alle pagine di MONITOR WF e DASHBOARD WF
 * Viene infatti prelevato il calendario di default per l'azienda e l'anno indicati (campo DEFAULT_X_MONITOR_WF a true)
 * @param params
 * @param opt
 */
export function useCalendarioChiusure(idAzienda: number | undefined, data1: DateTime, data2: DateTime, opt?: UseQueryOptions) {
    const isSameYear = (data1 && data2) ? (data1.year === data2.year) : false;

    /* ----- CALENDARIO CHIUSURE ----- */
    const params: CalendarioChiusureParams = {
        idAzienda,
        anno: isSameYear ? data1.year : 0
    };
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const isEmployee = user?.userClass === 'EMPLOYEE';

    const hook = useQuery<CalendarioChiusureRecord, Error>({
        queryKey: ['calendarioChiusure', params],
        queryFn: () => getCalendarioChiusureByAnno({idAzienda: params.idAzienda!, anno: params.anno}),
        retry: false,
        //enabled: !!(params.idAzienda && params.anno),
        enabled: !!(params.idAzienda), // 11/06/2024, controllo solo l'azienda, con anno a 0 il backend restituisce cmq l'eventuale calendario utile a testarne l'esistenza e l'abilitazione!
    })

    const hasCalendario = useMemo(() => !!(hook.data && hook.data.enabled), [hook.data]);

    const getEditabileFinoA = useCallback((currentDay: FPNetDate, periodo: Periodo): DateTime | null => {
        if (currentDay && hook.data && hook.data.enabled) {
            if (DateTime.fromISO(currentDay).startOf('day').month === periodo.mese) { // Month is 1-12
                return CalendarioChiusureRecord.getScadenza(hook.data, periodo.mese);
            }
        }
        return null;
    }, [hook.data]);

    /**
     * Verifica se per il giorno indicato le richieste di WF sono modificabili!
     */
    const isMonitorWFEditabile = useCallback((startDate: Date | null, endDate: Date | null, rifDate?: Date) => {
        return _isMonitorWFEditabile(isEmployee, hook.data, startDate, endDate, rifDate);
    }, [isEmployee, hook.data]);

    return {...hook, getEditabileFinoA, isMonitorWFEditabile, hasCalendario, isSameYear};
}